import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AgentChatSheet } from "./ai/AgentChatSheet";
import { useAuth } from "../contexts/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fetchAPI, formatFullName } from "../lib/utils";
import { programDomainLabel } from "../lib/programBuilderUtils";
import { hasShootingDomain } from "../lib/domainAccess";
import { shareTargets } from "../lib/coachUtils";
import {
	PROGRAM_COLORS,
	PROGRAM_COLOR_CLASSES,
	normalizeProgramColor,
	programColorLabelKey,
	usedProgramColors,
} from "../lib/programColors";
import { ProgramColorPicker } from "./programs/ProgramColorPicker";
import { ProgramColorStripe } from "./programs/ProgramColorMark";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	DialogDescription,
} from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
	Bot,
	Users,
	User,
	Check,
	ClipboardList,
	Plus,
	ChevronRight,
	Search,
	Trash2,
	Calendar,
	Download,
	FolderOpen,
	FolderPlus,
	FolderInput,
	Home,
	MoreVertical,
	Palette,
	Pencil,
	UserCog,
	Building2,
	ArrowUpDown,
	ArrowLeft,
	UserPlus,
	X,
	Layers,
	RotateCcw,
	Loader2,
	Copy,
	GripVertical,
	LayoutGrid,
	List,
	Share2,
	Eye,
	EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// La radice non ha un id: serve comunque un valore per evidenziarla come
// bersaglio di un trascinamento, perche' `null` qui vuol dire "nessun bersaglio".
const ROOT_DROP_ID = "__root__";

const CARD_INTERACTIVE_SELECTOR =
	'a,button,input,select,textarea,label,[role="menuitem"],[role="button"],[data-radix-popper-content-wrapper]';

/**
 * Il bollino "condiviso" nell'explorer.
 *
 * Prima lo stato di condivisione viveva solo dentro il dialog di condivisione:
 * per sapere se una cartella era uscita dal proprio perimetro bisognava aprirlo
 * riga per riga. Il `title` elenca i destinatari, cosi' la domanda successiva
 * ("con chi?") si risolve senza aprire niente.
 */
function ShareBadge({ item, t }) {
	const { coaches, teams, count } = shareTargets(item);
	if (count === 0) return null;

	const names = [...coaches, ...teams]
		.map((target) => target.name)
		.filter(Boolean);

	return (
		<span
			title={`${t("pbSharedWith")}: ${names.join(", ")}`}
			className="shrink-0 inline-flex items-center gap-1 rounded-sm border border-acid/40 bg-acid/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-acid"
		>
			<Share2 className="w-3 h-3" />
			{t("programSharedBadge")}
			{count > 1 ? ` · ${count}` : ""}
		</span>
	);
}

export function ProgramsTab() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { t, language } = useLanguage();
	const isAdminUser = user?.is_admin || user?.role === "admin";
	const returnContext = location.state?.programBuilderReturn || null;

	const formatCreatedDate = (dateStr) => {
		if (!dateStr) return "-";
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) return "-";
		const locale = language === "en" ? "en-US" : "it-IT";
		return date.toLocaleDateString(locale, {
			year: "numeric",
			month: "short",
			day: "2-digit",
		});
	};

	// ── State ──
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [programs, setPrograms] = useState([]);
	// Atleti e gruppi **della maschera scelta**: il server risponde col cono di
	// chi chiede (admin compreso) finche' non gli si chiede `scope=all`, cioe'
	// finche' l'admin non seleziona "tutti". Servono sia a risolvere i nomi degli
	// assegnati sia ai selettori di assegnazione: la maschera decide il cappello
	// per tutta la sezione, liste e controlli insieme.
	const [athletes, setAthletes] = useState([]);
	const [groups, setGroups] = useState([]);
	const [isCreateProgramOpen, setIsCreateProgramOpen] = useState(false);
	// L'assistente crea una bozza da zero: e' qui il suo posto naturale,
	// non dentro l'editor di un altro programma.
	const [isAiOpen, setIsAiOpen] = useState(false);
	const [isCreatingProgram, setIsCreatingProgram] = useState(false);
	const [exportingId, setExportingId] = useState(null);
	const [programSearch, setProgramSearch] = useState("");
	const [programSort, setProgramSort] = useState("newest");
	const [viewMode, setViewMode] = useState(() => {
		if (typeof window === "undefined") return "grid";
		const saved = window.localStorage.getItem("programs_tab_view_mode");
		return saved === "list" ? "list" : "grid";
	});
	const [programScope, setProgramScope] = useState(
		returnContext?.scope || "mine",
	);
	const [sharedCoachFilter, setSharedCoachFilter] = useState("all");
	const [showTrash, setShowTrash] = useState(false);
	const [trashedPrograms, setTrashedPrograms] = useState([]);
	const [loadingTrash, setLoadingTrash] = useState(false);
	const [isProgramRenameOpen, setIsProgramRenameOpen] = useState(false);
	const [renamingProgram, setRenamingProgram] = useState(null);
	const [programRenameTitle, setProgramRenameTitle] = useState("");
	// La descrizione si scriveva solo alla creazione: chi la ereditava da un
	// programma generato dall'AI non aveva nessun posto da cui correggerla.
	const [programRenameDescription, setProgramRenameDescription] = useState("");
	const [programRenameColor, setProgramRenameColor] = useState(null);
	// Filtro per colore della lista: una chiave della tavolozza o "all". Vive
	// nello stato della scheda, come la ricerca: e' una lente, non una vista.
	const [programColorFilter, setProgramColorFilter] = useState("all");

	// Folders
	const [folders, setFolders] = useState([]);
	const [currentFolderId, setCurrentFolderId] = useState(
		returnContext?.folderId || null,
	);
	const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
	const [editingFolder, setEditingFolder] = useState(null);
	const [moveProgramId, setMoveProgramId] = useState(null);
	const [folderName, setFolderName] = useState("");
	const [programAccessOptions, setProgramAccessOptions] = useState({
		coaches: [],
		teams: [],
	});
	const [isFolderShareOpen, setIsFolderShareOpen] = useState(false);
	const [sharingFolder, setSharingFolder] = useState(null);
	const [folderCoachSelection, setFolderCoachSelection] = useState([]);
	const [folderCoachTeamSelection, setFolderCoachTeamSelection] = useState([]);

	// Reorder (drag-and-drop)
	const [isReordering, setIsReordering] = useState(false);
	const dragSrcRef = useRef(null); // { type: "folder"|"program", id, index }
	const [dragOverId, setDragOverId] = useState(null);

	// Sposta (drag-and-drop fuori dalla modalita' riordino): qui il bersaglio e'
	// una cartella o un livello del percorso, non una posizione nella lista.
	const [moveTargetId, setMoveTargetId] = useState(null); // folder_id | ROOT_DROP_ID
	const [movingFolderId, setMovingFolderId] = useState(null);

	const [newProgram, setNewProgram] = useState({
		title: "",
		description: "",
		type: "individual",
		color: null,
		is_dated: true,
		athlete_ids: [],
		group_ids: [],
	});

	const preserveInitialFolderRef = useRef(Boolean(returnContext));

	// ── Data loading ──
	useEffect(() => {
		if (preserveInitialFolderRef.current) {
			preserveInitialFolderRef.current = false;
		} else {
			setCurrentFolderId(null);
		}
		if (programScope !== "all") {
			setSharedCoachFilter("all");
		}
		if (programScope !== "mine") {
			setIsReordering(false);
		}
		setShowTrash(false);
		loadData();
	}, [programScope]);

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.localStorage.setItem("programs_tab_view_mode", viewMode);
		}
	}, [viewMode]);

	// Dove porta il nome di un programma: l'editor se e' mio, il visualizzatore in
	// sola lettura se me lo hanno condiviso. Un programma condiviso non si scrive
	// (docs/coach-teams/DECISIONS.md §2), e aprirlo nel builder significherebbe
	// lasciare al coach dei campi su cui digitare per poi perdere tutto con un
	// 403 al salvataggio. Gli admin passano dall'editor: possono scrivere ovunque.
	const programHref = (program) => {
		const isOwner = program?.coach_id === user?.user_id;
		return isOwner || isAdminUser
			? `/programs/${program.program_id}`
			: `/programs/${program.program_id}/view`;
	};

	// Un programma condiviso si legge, si esporta e si duplica; rinominarlo,
	// spostarlo e cestinarlo sono scritture, e il server le rifiuta
	// (docs/coach-teams/DECISIONS.md §2). Le voci che fallirebbero non si
	// mostrano: stessa scelta gia' fatta per le cartelle con `canManageFolder`.
	const canEditProgram = (program) =>
		isAdminUser || program?.coach_id === user?.user_id;

	const getProgramBuilderReturnState = () => ({
		programBuilderReturn: {
			from: "programs-tab",
			scope: programScope,
			folderId: currentFolderId || null,
		},
	});

	// Su mobile il bersaglio utile e' tutta la card, non il solo titolo: con un
	// link di poche decine di pixel aprire un programma col dito diventa una
	// questione di mira. Le cartelle si comportavano gia' cosi'.
	// I click che atterrano su un controllo (menu, link "Apri", popover atleti,
	// checkbox) restano del controllo, cosi' il menu contestuale continua a
	// funzionare senza stopPropagation sparsi.
	const openProgramFromCard = (program, e) => {
		if (isReordering) return;
		if (e.target?.closest?.(CARD_INTERACTIVE_SELECTOR)) return;
		if (window.getSelection?.()?.toString()) return;
		navigate(programHref(program), { state: getProgramBuilderReturnState() });
	};

	const loadData = async () => {
		const isInitialLoad = loading;
		if (!isInitialLoad) {
			setIsRefreshing(true);
		}
		try {
			// La maschera di piattaforma va dichiarata al server, e solo l'admin
			// la ottiene: senza `scope=all` anche lui riceve il proprio cono.
			const wideScope = isAdminUser && programScope === "all";
			const listQs = wideScope ? "?scope=all" : "";
			const [
				programsData,
				athletesData,
				groupsData,
				foldersData,
				programAccessOptionsData,
			] = await Promise.all([
				fetchAPI(`/programs?scope=${programScope}&compact=true`).catch(
					() => [],
				),
				fetchAPI(`/users/athletes${listQs}`).catch(() => []),
				fetchAPI(`/groups${listQs}`).catch(() => []),
				fetchAPI(`/programs/folders/list?scope=${programScope}`).catch(
					() => [],
				),
				fetchAPI(`/programs/access-options${listQs}`).catch(() => ({
					coaches: [],
					teams: [],
				})),
			]);
			setPrograms(programsData || []);
			setAthletes(athletesData || []);
			setGroups(groupsData || []);
			setFolders(foldersData || []);
			setProgramAccessOptions(
				programAccessOptionsData || { coaches: [], teams: [] },
			);
		} catch (error) {
			console.error("Failed to load programs data:", error);
		} finally {
			setLoading(false);
			setIsRefreshing(false);
		}
	};

	// ── Program handlers ──

	const handleCreateProgram = async (e) => {
		e.preventDefault();
		if (isCreatingProgram) return;
		setIsCreatingProgram(true);
		try {
			const result = await fetchAPI("/programs", {
				method: "POST",
				body: JSON.stringify({
					...newProgram,
					folder_id: currentFolderId || null,
					// New dated programs release each week by themselves, the Sunday
					// before; the coach changes it in the builder's Details. The
					// server default stays off, so only this dialog switches it on.
					auto_publish_weeks: Boolean(newProgram.is_dated),
					auto_publish_lead_days: 1,
				}),
			});
			toast.success("Programma creato con successo");
			setIsCreateProgramOpen(false);
			setNewProgram({
				title: "",
				description: "",
				type: "individual",
				color: null,
				is_dated: true,
				athlete_ids: [],
				group_ids: [],
			});
			navigate(`/programs/${result.program_id}`, {
				state: getProgramBuilderReturnState(),
			});
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setIsCreatingProgram(false);
		}
	};

	const handleDeleteProgram = async (programId, e) => {
		e.preventDefault();
		e.stopPropagation();
		try {
			await fetchAPI(`/programs/${programId}`, { method: "DELETE" });
			toast.success(t("movedToTrash"));
			setPrograms((prev) => prev.filter((p) => p.program_id !== programId));
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const loadTrash = async () => {
		setLoadingTrash(true);
		try {
			// Il cestino segue la maschera scelta, come gli elenchi: "tutti"
			// mostra quello di piattaforma, altrimenti il proprio.
			const data = await fetchAPI(
				`/programs/trash${isAdminUser && programScope === "all" ? "?scope=all" : ""}`,
			);
			setTrashedPrograms(data || []);
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setLoadingTrash(false);
		}
	};

	const handleOpenTrash = () => {
		setShowTrash(true);
		loadTrash();
	};

	const handleRestoreProgram = async (programId, e) => {
		e.preventDefault();
		e.stopPropagation();
		try {
			await fetchAPI(`/programs/${programId}/restore`, { method: "POST" });
			toast.success(t("programRestored"));
			setTrashedPrograms((prev) =>
				prev.filter((p) => p.program_id !== programId),
			);
			const fresh = await fetchAPI(
				`/programs?scope=${programScope}&compact=true`,
			).catch(() => []);
			setPrograms(fresh || []);
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handlePermanentDelete = async (programId, e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!window.confirm(t("confirmPermanentDelete"))) return;
		try {
			await fetchAPI(`/programs/${programId}/permanent`, { method: "DELETE" });
			toast.success(t("programPermanentlyDeleted"));
			setTrashedPrograms((prev) =>
				prev.filter((p) => p.program_id !== programId),
			);
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleExportPDF = async (programId, e) => {
		e.preventDefault();
		e.stopPropagation();
		setExportingId(programId);
		try {
			const response = await fetch(
				`${backendUrl}/api/programs/${programId}/export-pdf`,
				{
					credentials: "include",
					headers: {
						Authorization: `Bearer ${
							localStorage.getItem("session_token") || ""
						}`,
					},
				},
			);
			if (!response.ok) throw new Error("Export failed");
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `program_${programId}.pdf`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
			toast.success("PDF esportato con successo!");
		} catch (error) {
			toast.error("Errore export: " + error.message);
		} finally {
			setExportingId(null);
		}
	};

	const handleDuplicateProgram = async (program, e) => {
		e.preventDefault();
		e.stopPropagation();
		try {
			await fetchAPI(`/programs/${program.program_id}/duplicate`, {
				method: "POST",
			});
			toast.success(t("programDuplicated"));
			const fresh = await fetchAPI(
				`/programs?scope=${programScope}&compact=true`,
			).catch(() => []);
			setPrograms(fresh || []);
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const openProgramRenameDialog = (program, e) => {
		e?.preventDefault?.();
		e?.stopPropagation?.();
		setRenamingProgram(program);
		setProgramRenameTitle((program?.title || "").trim());
		setProgramRenameDescription(program?.description || "");
		setProgramRenameColor(normalizeProgramColor(program?.color));
		setIsProgramRenameOpen(true);
	};

	const handleRenameProgram = async () => {
		if (!renamingProgram) return;
		const nextTitle = programRenameTitle.trim();
		if (!nextTitle) {
			toast.error(t("programTitleRequired"));
			return;
		}
		// Svuotare la descrizione e' un'azione voluta: va salvata come null,
		// non lasciata com'era.
		const nextDescription = programRenameDescription.trim() || null;
		const nextColor = normalizeProgramColor(programRenameColor);

		try {
			await fetchAPI(`/programs/${renamingProgram.program_id}`, {
				method: "PUT",
				body: JSON.stringify({
					title: nextTitle,
					description: nextDescription,
					color: nextColor,
				}),
			});
			setPrograms((prev) =>
				prev.map((program) =>
					program.program_id === renamingProgram.program_id
						? {
								...program,
								title: nextTitle,
								description: nextDescription,
								color: nextColor,
								updated_at: new Date().toISOString(),
							}
						: program,
				),
			);
			toast.success(t("programSaved"));
			setIsProgramRenameOpen(false);
			setRenamingProgram(null);
			setProgramRenameTitle("");
			setProgramRenameDescription("");
			setProgramRenameColor(null);
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	// Cambio colore dal menu della card: un gesto solo, senza aprire il dialog
	// di modifica (H7). Ottimistico, e torna indietro se il server rifiuta.
	const handleSetProgramColor = async (program, color) => {
		const nextColor = normalizeProgramColor(color);
		const previousColor = normalizeProgramColor(program?.color);
		if (nextColor === previousColor) return;
		const apply = (value) =>
			setPrograms((prev) =>
				prev.map((item) =>
					item.program_id === program.program_id
						? { ...item, color: value }
						: item,
				),
			);
		apply(nextColor);
		try {
			await fetchAPI(`/programs/${program.program_id}`, {
				method: "PUT",
				body: JSON.stringify({ color: nextColor }),
			});
		} catch (error) {
			apply(previousColor);
			toast.error("Errore: " + error.message);
		}
	};

	// Sottomenu «Colore» del menu della card: una voce per colore, pallino +
	// nome, la scelta corrente con la spunta. Stesso elenco in lista e griglia.
	const renderProgramColorMenu = (program) => {
		const current = normalizeProgramColor(program?.color);
		const options = [null, ...PROGRAM_COLORS];
		return (
			<DropdownMenuSub>
				<DropdownMenuSubTrigger
					onClick={(e) => e.stopPropagation()}
					data-testid={`program-color-menu-${program.program_id}`}
				>
					<Palette className="w-3.5 h-3.5 mr-2" />
					{t("programColor")}
					{current && (
						<span
							aria-hidden="true"
							className={`ml-2 inline-block w-2.5 h-2.5 rounded-full ${PROGRAM_COLOR_CLASSES[current].bg}`}
						/>
					)}
				</DropdownMenuSubTrigger>
				<DropdownMenuSubContent className="bg-card border-border">
					{options.map((color) => (
						<DropdownMenuItem
							key={color ?? "none"}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleSetProgramColor(program, color);
							}}
							data-testid={`program-color-option-${color ?? "none"}`}
						>
							<span
								aria-hidden="true"
								className={`mr-2 inline-block w-3 h-3 rounded-full ${
									color
										? PROGRAM_COLOR_CLASSES[color].bg
										: "border border-dashed border-muted-foreground/60"
								}`}
							/>
							<span className="flex-1">
								{color ? t(programColorLabelKey(color)) : t("programColorNone")}
							</span>
							{current === color && (
								<Check
									className="w-3.5 h-3.5 ml-3 text-volt"
									aria-hidden="true"
								/>
							)}
						</DropdownMenuItem>
					))}
				</DropdownMenuSubContent>
			</DropdownMenuSub>
		);
	};

	const toggleAthleteSelection = (athleteId) => {
		setNewProgram((prev) => ({
			...prev,
			athlete_ids: prev.athlete_ids.includes(athleteId)
				? prev.athlete_ids.filter((id) => id !== athleteId)
				: [...prev.athlete_ids, athleteId],
		}));
	};

	const toggleGroupForProgram = (groupId) => {
		setNewProgram((prev) => ({
			...prev,
			group_ids: (prev.group_ids || []).includes(groupId)
				? prev.group_ids.filter((id) => id !== groupId)
				: [...(prev.group_ids || []), groupId],
		}));
	};

	// ── Folder handlers ──

	const breadcrumb = useMemo(() => {
		const path = [];
		let id = currentFolderId;
		const folderMap = {};
		folders.forEach((f) => (folderMap[f.folder_id] = f));
		while (id) {
			const f = folderMap[id];
			if (!f) break;
			path.unshift(f);
			id = f.parent_id;
		}
		return path;
	}, [currentFolderId, folders]);

	const currentFolders = useMemo(() => {
		let result = folders
			.filter((f) =>
				currentFolderId ? f.parent_id === currentFolderId : !f.parent_id,
			)
			.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

		if (programScope === "all" && sharedCoachFilter !== "all") {
			result = result.filter((folder) => {
				const coachIds = Array.from(
					new Set([
						...(folder?.coach_ids || []),
						...(folder?.coach_id ? [folder.coach_id] : []),
					]),
				);
				return coachIds.includes(sharedCoachFilter);
			});
		}
		return result;
	}, [folders, currentFolderId, programScope, sharedCoachFilter]);

	// Gli id delle cartelle che vedo: serve a non far sparire un programma il cui
	// `folder_id` punta a una cartella che non e' condivisa con me.
	const visibleFolderIds = useMemo(
		() => new Set(folders.map((f) => f.folder_id)),
		[folders],
	);

	const currentPrograms = useMemo(() => {
		let filtered = programs.filter((p) => {
			if (currentFolderId) return p.folder_id === currentFolderId;
			// In radice: i programmi senza cartella **e** quelli la cui cartella non
			// e' visibile. Un programma condiviso singolarmente vive spesso in una
			// cartella del proprietario che non e' condivisa: filtrandolo per
			// cartella spariva del tutto, non comparendo ne' in radice ne' altrove.
			return !p.folder_id || !visibleFolderIds.has(p.folder_id);
		});

		if (programScope === "all" && sharedCoachFilter !== "all") {
			filtered = filtered.filter((program) => {
				const coachIds = Array.from(
					new Set([
						...(program?.coach_ids || []),
						...(program?.coach_id ? [program.coach_id] : []),
					]),
				);
				return coachIds.includes(sharedCoachFilter);
			});
		}

		if (programSearch.trim()) {
			const q = programSearch.toLowerCase();
			filtered = filtered.filter(
				(p) =>
					p.title?.toLowerCase().includes(q) ||
					p.description?.toLowerCase().includes(q),
			);
		}
		if (programColorFilter !== "all") {
			filtered = filtered.filter(
				(p) => normalizeProgramColor(p.color) === programColorFilter,
			);
		}
		filtered.sort((a, b) => {
			const dateA = new Date(a.created_at || 0);
			const dateB = new Date(b.created_at || 0);
			if (programSort === "newest") return dateB - dateA;
			if (programSort === "oldest") return dateA - dateB;
			if (programSort === "alpha")
				return (a.title || "").localeCompare(b.title || "");
			if (programSort === "manual")
				return (a.sort_order ?? 0) - (b.sort_order ?? 0);
			return dateB - dateA;
		});
		return filtered;
	}, [
		programs,
		currentFolderId,
		programSearch,
		programSort,
		programScope,
		sharedCoachFilter,
		visibleFolderIds,
		programColorFilter,
	]);

	// I colori in uso nell'ambito aperto (non solo nella cartella: il filtro
	// e' una lente sulla libreria, e cambiare cartella non deve farlo sparire).
	// Senza programmi colorati il filtro non compare: la pagina resta com'era.
	const availableProgramColors = useMemo(
		() => usedProgramColors(programs),
		[programs],
	);
	useEffect(() => {
		if (
			programColorFilter !== "all" &&
			!availableProgramColors.includes(programColorFilter)
		) {
			setProgramColorFilter("all");
		}
	}, [availableProgramColors, programColorFilter]);

	const allFolderOptions = useMemo(() => {
		const folderMap = {};
		folders.forEach((f) => (folderMap[f.folder_id] = f));
		const getPath = (f) => {
			const parts = [f.name];
			let pid = f.parent_id;
			while (pid && folderMap[pid]) {
				parts.unshift(folderMap[pid].name);
				pid = folderMap[pid].parent_id;
			}
			return parts.join(" / ");
		};
		return folders.map((f) => ({ ...f, path: getPath(f) }));
	}, [folders]);

	const handleCreateFolder = async () => {
		if (!folderName.trim()) return;
		try {
			await fetchAPI("/programs/folders", {
				method: "POST",
				body: JSON.stringify({
					name: folderName.trim(),
					parent_id: currentFolderId || null,
				}),
			});
			toast.success("Cartella creata");
			setFolderName("");
			setIsFolderDialogOpen(false);
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleRenameFolder = async () => {
		if (!folderName.trim() || !editingFolder) return;
		try {
			await fetchAPI(`/programs/folders/${editingFolder.folder_id}`, {
				method: "PUT",
				body: JSON.stringify({ name: folderName.trim() }),
			});
			toast.success("Cartella rinominata");
			setFolderName("");
			setEditingFolder(null);
			setIsFolderDialogOpen(false);
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleDeleteFolder = async (folderId, e) => {
		e.stopPropagation();
		if (
			!window.confirm(
				"Eliminare questa cartella? I contenuti verranno spostati nella cartella superiore.",
			)
		)
			return;
		try {
			await fetchAPI(`/programs/folders/${folderId}`, { method: "DELETE" });
			toast.success("Cartella eliminata");
			if (currentFolderId === folderId) setCurrentFolderId(null);
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const openFolderDialog = (folder = null) => {
		setEditingFolder(folder);
		setFolderName(folder ? folder.name : "");
		setIsFolderDialogOpen(true);
	};

	const getVisibilityReasonLabel = (item) => {
		switch (item?.visibility_reason) {
			case "owner":
				return t("programVisibilityOwner");
			case "shared_direct":
				return t("programVisibilitySharedDirect");
			case "shared_team":
				return t("programVisibilitySharedTeam");
			default:
				return t("programVisibilityShared");
		}
	};

	const openFolderShareDialog = (folder, e) => {
		e?.stopPropagation?.();
		setSharingFolder(folder);
		setFolderCoachSelection(folder?.coach_ids || []);
		setFolderCoachTeamSelection(folder?.coach_team_ids || []);
		setIsFolderShareOpen(true);
	};

	// ── Drag-and-drop reorder ──

	const handleDragStart = useCallback((type, id, index, e) => {
		dragSrcRef.current = { type, id, index };
		e.dataTransfer.effectAllowed = "move";
		// Firefox non avvia il trascinamento se il dataTransfer resta vuoto.
		e.dataTransfer.setData("text/plain", id);
	}, []);

	const handleDragOver = useCallback((type, id, e) => {
		e.preventDefault();
		if (dragSrcRef.current?.type === type) {
			e.dataTransfer.dropEffect = "move";
			setDragOverId(id);
		}
	}, []);

	const handleDrop = useCallback(
		async (type, toIndex, e) => {
			e.preventDefault();
			if (!dragSrcRef.current || dragSrcRef.current.type !== type) return;
			const fromIndex = dragSrcRef.current.index;
			if (fromIndex === toIndex) {
				setDragOverId(null);
				return;
			}
			if (type === "folder") {
				const reordered = [...currentFolders];
				const [moved] = reordered.splice(fromIndex, 1);
				reordered.splice(toIndex, 0, moved);
				setFolders((prev) => {
					const updated = [...prev];
					reordered.forEach((rf, i) => {
						const idx = updated.findIndex((f) => f.folder_id === rf.folder_id);
						if (idx !== -1) updated[idx] = { ...updated[idx], sort_order: i };
					});
					return updated;
				});
				try {
					await fetchAPI("/programs/folders/reorder", {
						method: "POST",
						body: JSON.stringify({
							folder_ids: reordered.map((f) => f.folder_id),
						}),
					});
				} catch {
					toast.error("Errore nel salvataggio dell'ordine");
				}
			} else {
				const reordered = [...currentPrograms];
				const [moved] = reordered.splice(fromIndex, 1);
				reordered.splice(toIndex, 0, moved);
				setPrograms((prev) => {
					const updated = [...prev];
					reordered.forEach((rp, i) => {
						const idx = updated.findIndex(
							(p) => p.program_id === rp.program_id,
						);
						if (idx !== -1) updated[idx] = { ...updated[idx], sort_order: i };
					});
					return updated;
				});
				try {
					await fetchAPI("/programs/reorder-list", {
						method: "POST",
						body: JSON.stringify({
							program_ids: reordered.map((p) => p.program_id),
						}),
					});
				} catch {
					toast.error("Errore nel salvataggio dell'ordine");
				}
			}
			setDragOverId(null);
			dragSrcRef.current = null;
		},
		[currentFolders, currentPrograms],
	);

	const handleDragEnd = useCallback(() => {
		setDragOverId(null);
		setMoveTargetId(null);
		dragSrcRef.current = null;
	}, []);

	// ── Drag-and-drop: sposta dentro una cartella ──

	// Una cartella non puo' finire dentro una sua discendente: il ramo si
	// staccherebbe dall'albero e sparirebbe da ogni vista. Il server rifiuta,
	// ma l'evidenziazione deve gia' dire di no prima del rilascio.
	const isFolderDescendantOf = useCallback(
		(candidateId, ancestorId) => {
			if (!candidateId || !ancestorId) return false;
			const folderMap = {};
			folders.forEach((f) => (folderMap[f.folder_id] = f));
			const seen = new Set();
			let current = candidateId;
			while (current && !seen.has(current)) {
				if (current === ancestorId) return true;
				seen.add(current);
				current = folderMap[current]?.parent_id || null;
			}
			return false;
		},
		[folders],
	);

	const canDropOn = useCallback(
		(src, targetFolderId) => {
			if (!src) return false;
			const target = targetFolderId || null;
			if (src.type === "folder") {
				const folder = folders.find((f) => f.folder_id === src.id);
				if (!folder) return false;
				if (!(isAdminUser || folder.coach_id === user?.user_id)) return false;
				if (src.id === target) return false;
				if (isFolderDescendantOf(target, src.id)) return false;
				return (folder.parent_id || null) !== target;
			}
			const program = programs.find((p) => p.program_id === src.id);
			if (!program || !canEditProgram(program)) return false;
			return (program.folder_id || null) !== target;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[folders, programs, isFolderDescendantOf, isAdminUser, user?.user_id],
	);

	const moveItemToFolder = useCallback(
		async (src, targetFolderId) => {
			if (!canDropOn(src, targetFolderId)) return;
			const target = targetFolderId || null;
			if (src.type === "folder") {
				const snapshot = folders;
				setFolders((prev) =>
					prev.map((f) =>
						f.folder_id === src.id ? { ...f, parent_id: target } : f,
					),
				);
				try {
					await fetchAPI(`/programs/folders/${src.id}`, {
						method: "PUT",
						body: JSON.stringify({ parent_id: target }),
					});
					toast.success(t("folderMoved"));
				} catch (error) {
					setFolders(snapshot);
					toast.error("Errore: " + error.message);
				}
				return;
			}
			const snapshot = programs;
			setPrograms((prev) =>
				prev.map((p) =>
					p.program_id === src.id ? { ...p, folder_id: target } : p,
				),
			);
			try {
				await fetchAPI(`/programs/${src.id}`, {
					method: "PUT",
					body: JSON.stringify({ folder_id: target }),
				});
				toast.success(t("programMoved"));
			} catch (error) {
				setPrograms(snapshot);
				toast.error("Errore: " + error.message);
			}
		},
		[canDropOn, folders, programs, t],
	);

	const handleMoveDragOver = useCallback(
		(targetFolderId, e) => {
			if (isReordering) return;
			if (!canDropOn(dragSrcRef.current, targetFolderId)) return;
			// Senza preventDefault il browser non considera l'elemento un bersaglio
			// valido e il rilascio non arriva mai.
			e.preventDefault();
			e.stopPropagation();
			e.dataTransfer.dropEffect = "move";
			setMoveTargetId(targetFolderId || ROOT_DROP_ID);
		},
		[canDropOn, isReordering],
	);

	const handleMoveDragLeave = useCallback((targetFolderId, e) => {
		// `dragleave` scatta anche passando sui figli: l'evidenziazione si spegne
		// solo quando il puntatore esce davvero dalla card.
		if (e.currentTarget.contains(e.relatedTarget)) return;
		const id = targetFolderId || ROOT_DROP_ID;
		setMoveTargetId((prev) => (prev === id ? null : prev));
	}, []);

	const handleMoveDrop = useCallback(
		(targetFolderId, e) => {
			if (isReordering) return;
			e.preventDefault();
			e.stopPropagation();
			const src = dragSrcRef.current;
			dragSrcRef.current = null;
			setMoveTargetId(null);
			moveItemToFolder(src, targetFolderId);
		},
		[isReordering, moveItemToFolder],
	);

	// Lo stesso spostamento resta raggiungibile dal menu: il trascinamento
	// HTML5 non esiste sul touch, e le cartelle vivono anche su tablet.
	const movingItem = moveProgramId
		? { type: "program", id: moveProgramId }
		: movingFolderId
			? { type: "folder", id: movingFolderId }
			: null;

	const closeMoveDialog = () => {
		setMoveProgramId(null);
		setMovingFolderId(null);
	};

	const handleMoveFromDialog = (targetFolderId) => {
		const item = movingItem;
		closeMoveDialog();
		moveItemToFolder(item, targetFolderId);
	};

	const toggleReorder = () => {
		// L'ordinamento manuale vale solo sui propri contenuti: `reorder-list` e
		// `folders/reorder` filtrano per proprieta', quindi trascinare nella vista
		// "Condivisi" non salvava nulla — senza errori, senza avvisi, e all'aggiornamento
		// successivo tutto tornava com'era.
		if (programScope !== "mine") {
			toast.info(t("programReorderOwnOnly"));
			return;
		}
		setIsReordering((prev) => {
			if (!prev) setProgramSort("manual");
			return !prev;
		});
	};

	const toggleFolderCoach = (coachId) => {
		setFolderCoachSelection((prev) =>
			prev.includes(coachId)
				? prev.filter((id) => id !== coachId)
				: [...prev, coachId],
		);
	};

	const toggleFolderCoachTeam = (teamId) => {
		setFolderCoachTeamSelection((prev) =>
			prev.includes(teamId)
				? prev.filter((id) => id !== teamId)
				: [...prev, teamId],
		);
	};

	const handleSaveFolderSharing = async () => {
		if (!sharingFolder) return;
		try {
			await fetchAPI(`/programs/folders/${sharingFolder.folder_id}`, {
				method: "PUT",
				body: JSON.stringify({
					coach_ids: folderCoachSelection,
					coach_team_ids: folderCoachTeamSelection,
				}),
			});
			toast.success("Condivisione cartella aggiornata");
			setIsFolderShareOpen(false);
			setSharingFolder(null);
			loadData();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	// ── Render ──

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="spinner"></div>
			</div>
		);
	}

	return (
		<div
			className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8"
			data-testid="programs-tab"
		>
			<div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
				<div>
					<h2 className="font-heading text-2xl">{t("programs")}</h2>
					<p className="text-sm text-muted-foreground">{t("managePrograms")}</p>
					{/* Ambito: su mobile e' un segmented control compatto,
					    cosi' non mangia una riga intera prima della lista. */}
					<div className="flex items-center gap-2 mt-3 overflow-x-auto no-scrollbar">
						<Button
							size="sm"
							variant={programScope === "mine" ? "default" : "outline"}
							className="rounded-sm shrink-0"
							onClick={() => setProgramScope("mine")}
						>
							{t("programScopeMine")}
						</Button>
						<Button
							size="sm"
							variant={programScope === "shared" ? "default" : "outline"}
							className="rounded-sm shrink-0"
							onClick={() => setProgramScope("shared")}
						>
							{t("programScopeShared")}
						</Button>
						{isAdminUser && (
							<Button
								size="sm"
								variant={programScope === "all" ? "default" : "outline"}
								className="rounded-sm shrink-0"
								onClick={() => setProgramScope("all")}
							>
								{t("programScopeAll")}
							</Button>
						)}
					</div>
					{isRefreshing && (
						<div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
							<span>Caricamento programmi...</span>
						</div>
					)}
				</div>
				<div className="flex gap-2 items-center flex-wrap">
					{programScope === "mine" && (
						<>
							{/* Su mobile cestino/riordina/cartella finiscono in un menu:
							    tre bottoni pieni occupavano due righe prima della lista. */}
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="outline"
										size="icon"
										className="rounded-sm sm:hidden shrink-0"
										aria-label={t("programsMoreActions")}
									>
										<MoreVertical className="w-4 h-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="end"
									className="bg-card border-border"
								>
									<DropdownMenuItem
										onClick={() => {
											if (!showTrash) {
												handleOpenTrash();
											} else {
												setShowTrash(false);
											}
										}}
									>
										<Trash2 className="w-3.5 h-3.5 mr-2" />
										{t("trashBin")}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={toggleReorder}>
										<GripVertical className="w-3.5 h-3.5 mr-2" />
										{t(isReordering ? "programReorderDone" : "programReorder")}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => openFolderDialog()}>
										<FolderPlus className="w-3.5 h-3.5 mr-2" />
										{t("folder")}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
							<Button
								variant="ghost"
								className={`rounded-sm relative hidden sm:inline-flex ${showTrash ? "text-blaze border border-blaze/40" : "text-muted-foreground hover:text-foreground"}`}
								onClick={() => {
									if (!showTrash) {
										handleOpenTrash();
									} else {
										setShowTrash(false);
									}
								}}
							>
								<Trash2 className="w-4 h-4 mr-2" />
								{t("trashBin")}
							</Button>
							<Button
								variant={isReordering ? "outline" : "outline"}
								className={`rounded-sm hidden sm:inline-flex ${isReordering ? "border-acid/50 text-acid" : "border-dashed border-muted-foreground/30 text-muted-foreground hover:text-foreground"}`}
								onClick={toggleReorder}
							>
								<GripVertical className="w-4 h-4 mr-2" />
								{t(isReordering ? "programReorderDone" : "programReorder")}
							</Button>
							<Button
								variant="outline"
								className="rounded-sm border-dashed border-volt/50 text-volt hidden sm:inline-flex"
								onClick={() => openFolderDialog()}
							>
								<FolderPlus className="w-4 h-4 mr-2" />
								{t("folder")}
							</Button>
						</>
					)}
					{isAdminUser && programScope === "all" && (
						<Select
							value={sharedCoachFilter}
							onValueChange={setSharedCoachFilter}
						>
							<SelectTrigger className="w-56 bg-secondary border-border rounded-sm text-foreground">
								<SelectValue placeholder="Filtro coach" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">Tutti i coach</SelectItem>
								{programAccessOptions?.coaches?.map((coach) => (
									<SelectItem
										key={coach.user_id || coach._id}
										value={coach.user_id || coach._id}
									>
										{coach.name || coach.email}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
					{programScope === "mine" && (
						<Button
							variant="outline"
							onClick={() => setIsAiOpen(true)}
							className="rounded-sm border-volt/40 text-volt hover:bg-volt/10 flex-1 sm:flex-none"
							data-testid="new-program-ai-btn"
						>
							<Bot className="w-4 h-4 mr-2" />
							Crea con AI
						</Button>
					)}
					{programScope === "mine" && (
						<Dialog
							open={isCreateProgramOpen}
							onOpenChange={setIsCreateProgramOpen}
						>
							<DialogTrigger asChild>
								<Button
									className="bg-volt hover:bg-volt/90 rounded-sm flex-1 sm:flex-none"
									data-testid="new-program-btn"
								>
									<Plus className="w-4 h-4 mr-2" />
									{t("newProgram")}
								</Button>
							</DialogTrigger>
							<DialogContent className="bg-card border-border max-w-lg">
								<DialogDescription className="hidden" />
								<DialogHeader>
									<DialogTitle className="font-heading">
										{t("newProgram")}
									</DialogTitle>
								</DialogHeader>
								<form onSubmit={handleCreateProgram} className="space-y-4">
									<div className="space-y-2">
										<Label className="label-tactical">
											{t("titleLabel")} *
										</Label>
										<Input
											value={newProgram.title}
											onChange={(e) =>
												setNewProgram({
													...newProgram,
													title: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm"
											placeholder="Programma Forza 4 Settimane"
											required
											data-testid="program-title-input"
										/>
									</div>
									<div className="space-y-2">
										<Label className="label-tactical">{t("type")}</Label>
										<Select
											value={newProgram.type}
											onValueChange={(value) =>
												setNewProgram({ ...newProgram, type: value })
											}
										>
											<SelectTrigger
												className="bg-secondary border-transparent rounded-sm"
												data-testid="program-type-select"
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												<SelectItem value="individual">
													{t("individual")}
												</SelectItem>
												<SelectItem value="group">{t("group")}</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-2">
										<div className="flex items-center gap-2">
											<Label className="label-tactical">{t("mode")}</Label>
											<span className="rounded-sm border border-volt/30 bg-volt/5 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-volt">
												{t("datedModeRecommended")}
											</span>
										</div>
										<Select
											value={newProgram.is_dated ? "dated" : "undated"}
											onValueChange={(value) =>
												setNewProgram({
													...newProgram,
													is_dated: value === "dated",
												})
											}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm">
												<SelectValue />
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												<SelectItem value="dated">{t("datedMode")}</SelectItem>
												<SelectItem value="undated">
													{t("undatedMode")}
												</SelectItem>
											</SelectContent>
										</Select>
										{/* The consequence of the choice, not the label again: dated
										    weeks appear and hide by themselves; undated ones are
										    published by hand. */}
										<p className="text-xs text-muted-foreground">
											{newProgram.is_dated
												? t("datedModeHint")
												: t("undatedModeHint")}
										</p>
									</div>
									<div className="space-y-2">
										<Label className="label-tactical">{t("description")}</Label>
										<Textarea
											value={newProgram.description}
											onChange={(e) =>
												setNewProgram({
													...newProgram,
													description: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm min-h-[80px]"
											placeholder="Descrizione del programma..."
										/>
									</div>
									{/* Il colore e' un extra: una riga di pallini, niente campo
									    in piu' da compilare. «Nessuno» e' il predefinito. */}
									<div className="space-y-1.5">
										<div className="flex items-center gap-2">
											<Label className="label-tactical">
												{t("programColor")}
											</Label>
											<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
												{t("programColorOptional")}
											</span>
										</div>
										<ProgramColorPicker
											value={newProgram.color}
											onChange={(color) =>
												setNewProgram({ ...newProgram, color })
											}
										/>
										<p className="text-xs text-muted-foreground">
											{t("programColorHint")}
										</p>
									</div>
									{newProgram.type === "individual" && athletes.length > 0 && (
										<div className="space-y-2">
											<Label className="label-tactical">
												{t("assignedAthletes")}
											</Label>
											<div className="max-h-40 overflow-y-auto space-y-2 p-2 bg-secondary rounded-sm">
												{athletes.map((athlete) => (
													<label
														key={athlete.user_id}
														className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-sm cursor-pointer"
													>
														<input
															type="checkbox"
															checked={newProgram.athlete_ids.includes(
																athlete.user_id,
															)}
															onChange={() =>
																toggleAthleteSelection(athlete.user_id)
															}
															className="rounded border-border shrink-0"
														/>
														{/* Nome ed email su due righe: affiancati, su mobile
														    l'email sfondava la modale invece di troncarsi. */}
														<div className="min-w-0 flex-1">
															<p className="text-sm truncate">
																{formatFullName(athlete) || athlete.name}
															</p>
															<p className="text-xs text-muted-foreground truncate">
																{athlete.email}
															</p>
														</div>
													</label>
												))}
											</div>
										</div>
									)}
									{newProgram.type === "group" && groups.length > 0 && (
										<div className="space-y-2">
											<Label className="label-tactical">
												{t("assignGroups")}
											</Label>
											<div className="max-h-40 overflow-y-auto space-y-2 p-2 bg-secondary rounded-sm">
												{groups.map((group) => (
													<label
														key={group.group_id}
														className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-sm cursor-pointer"
													>
														<input
															type="checkbox"
															checked={(newProgram.group_ids || []).includes(
																group.group_id,
															)}
															onChange={() =>
																toggleGroupForProgram(group.group_id)
															}
															className="rounded border-border shrink-0"
														/>
														<div className="flex items-center gap-2 min-w-0 flex-1">
															<FolderOpen className="w-4 h-4 text-volt shrink-0" />
															<span className="text-sm truncate">
																{group.name}
															</span>
														</div>
														<span className="text-xs text-muted-foreground shrink-0">
															{(group.athlete_ids || []).length} atleti
														</span>
													</label>
												))}
											</div>
										</div>
									)}
									<Button
										type="submit"
										disabled={isCreatingProgram}
										className="w-full bg-volt hover:bg-volt/90 rounded-sm"
										data-testid="create-program-btn"
									>
										{isCreatingProgram && (
											<Loader2 className="w-4 h-4 mr-2 animate-spin" />
										)}
										{t("createProgram")}
									</Button>
								</form>
							</DialogContent>
						</Dialog>
					)}
				</div>
			</div>
			{/* Breadcrumb */}
			{!showTrash && (
				<>
					<nav className="flex items-center gap-1.5 text-sm mb-5 overflow-x-auto pb-1">
						<button
							onClick={() => setCurrentFolderId(null)}
							onDragOver={(e) => handleMoveDragOver(null, e)}
							onDragLeave={(e) => handleMoveDragLeave(null, e)}
							onDrop={(e) => handleMoveDrop(null, e)}
							className={`flex items-center gap-1 px-2 py-1 rounded-sm shrink-0 ${
								!currentFolderId
									? "text-volt font-medium"
									: "text-muted-foreground hover:text-foreground"
							} ${moveTargetId === ROOT_DROP_ID ? "ring-1 ring-volt bg-volt/10" : ""}`}
						>
							<Home className="w-3.5 h-3.5" />
							Root
						</button>
						{breadcrumb.map((f) => (
							<span
								key={f.folder_id}
								className="flex items-center gap-1.5 shrink-0"
							>
								<ChevronRight className="w-3 h-3 text-muted-foreground" />
								<button
									onClick={() => setCurrentFolderId(f.folder_id)}
									onDragOver={(e) => handleMoveDragOver(f.folder_id, e)}
									onDragLeave={(e) => handleMoveDragLeave(f.folder_id, e)}
									onDrop={(e) => handleMoveDrop(f.folder_id, e)}
									className={`px-2 py-1 rounded-sm ${
										f.folder_id === currentFolderId
											? "text-volt font-medium"
											: "text-muted-foreground hover:text-foreground"
									} ${moveTargetId === f.folder_id ? "ring-1 ring-volt bg-volt/10" : ""}`}
								>
									{f.name}
								</button>
							</span>
						))}
					</nav>

					{!isReordering && currentFolders.length > 0 && (
						<p className="hidden lg:block text-[11px] text-muted-foreground mb-4">
							{t("programDragMoveHint")}
						</p>
					)}

					{/* Search & Sort */}
					{/* Reorder mode banner */}
					{isReordering && (
						<div className="flex items-center gap-2 mb-4 px-3 py-2 bg-acid/10 border border-acid/30 rounded-sm text-acid text-sm">
							<GripVertical className="w-4 h-4 shrink-0" />
							<span>
								Trascina cartelle e programmi per riordinarli. L'ordine viene
								salvato automaticamente.
							</span>
						</div>
					)}

					{/* Search & Sort — hidden in reorder mode */}
					{!isReordering && (
						<div className="flex flex-col sm:flex-row gap-3 mb-5">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
								<Input
									value={programSearch}
									onChange={(e) => setProgramSearch(e.target.value)}
									className="bg-secondary border-transparent rounded-sm pl-9"
									placeholder={t("programSearchPlaceholder")}
								/>
								{programSearch && (
									<button
										onClick={() => setProgramSearch("")}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										<X className="w-4 h-4" />
									</button>
								)}
							</div>
							{/* Ordinamento e vista stanno sulla stessa riga anche su mobile:
							    la ricerca si prende la riga sopra, da sola. */}
							<div className="flex items-center gap-2 flex-wrap sm:contents">
								{/* Filtro per colore: compare solo se qualche programma e'
								    colorato, e mostra solo i colori in uso. Un tocco filtra,
								    un altro sullo stesso toglie il filtro. Su mobile ha una
								    riga sua: con ordinamento e vista accanto non ci stava. */}
								{availableProgramColors.length > 0 && (
									<div className="basis-full sm:basis-auto">
										<div
											role="group"
											aria-label={t("programColorFilter")}
											title={t("programColorFilter")}
											className="inline-flex items-center gap-0.5 rounded-sm border border-border bg-secondary p-1 shrink-0"
											data-testid="program-color-filter"
										>
											{availableProgramColors.map((color) => {
												const active = programColorFilter === color;
												const label = t(programColorLabelKey(color));
												return (
													<button
														key={color}
														type="button"
														aria-pressed={active}
														aria-label={label}
														title={label}
														onClick={() =>
															setProgramColorFilter(active ? "all" : color)
														}
														className={`h-8 w-8 inline-flex items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/60 ${
															active
																? "bg-background"
																: "hover:bg-background/60"
														}`}
													>
														<span
															className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full ${PROGRAM_COLOR_CLASSES[color].bg} ${
																active
																	? `ring-2 ring-offset-1 ring-offset-background ${PROGRAM_COLOR_CLASSES[color].ring}`
																	: "opacity-80"
															}`}
														>
															{active && (
																<Check
																	className="w-2.5 h-2.5 text-black/80"
																	strokeWidth={3}
																	aria-hidden="true"
																/>
															)}
														</span>
													</button>
												);
											})}
										</div>
									</div>
								)}
								<Select value={programSort} onValueChange={setProgramSort}>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm flex-1 sm:flex-none sm:w-[200px]">
										<ArrowUpDown className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="newest">
											{t("programSortNewest")}
										</SelectItem>
										<SelectItem value="oldest">
											{t("programSortOldest")}
										</SelectItem>
										<SelectItem value="alpha">
											{t("programSortAlphabetical")}
										</SelectItem>
										<SelectItem value="manual">{t("manual")}</SelectItem>
									</SelectContent>
								</Select>
								<div className="flex items-center gap-1 rounded-sm border border-border p-1 bg-secondary shrink-0">
									<Button
										type="button"
										variant={viewMode === "grid" ? "default" : "ghost"}
										size="sm"
										className="h-8 rounded-sm px-2"
										onClick={() => setViewMode("grid")}
										title={t("viewModeGrid")}
									>
										<LayoutGrid className="w-4 h-4" />
									</Button>
									<Button
										type="button"
										variant={viewMode === "list" ? "default" : "ghost"}
										size="sm"
										className="h-8 rounded-sm px-2"
										onClick={() => setViewMode("list")}
										title={t("viewModeList")}
									>
										<List className="w-4 h-4" />
									</Button>
								</div>
							</div>
						</div>
					)}

					{/* Folders */}
					{currentFolders.length > 0 && (
						<div
							className={`${
								viewMode === "grid"
									? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
									: "space-y-2"
							} mb-6`}
						>
							{currentFolders.map((folder, idx) =>
								(() => {
									const canManageFolder =
										isAdminUser || folder.coach_id === user?.user_id;
									const isFolderListMode = viewMode === "list";
									const subFolderCount = folders.filter(
										(f) => f.parent_id === folder.folder_id,
									).length;
									const programCount = programs.filter(
										(p) => p.folder_id === folder.folder_id,
									).length;
									const isDragOver = isReordering
										? dragOverId === folder.folder_id &&
											dragSrcRef.current?.type === "folder"
										: moveTargetId === folder.folder_id;
									return (
										<Card
											key={folder.folder_id}
											draggable={isReordering || canManageFolder}
											onDragStart={(e) =>
												handleDragStart("folder", folder.folder_id, idx, e)
											}
											onDragOver={
												isReordering
													? (e) => handleDragOver("folder", folder.folder_id, e)
													: (e) => handleMoveDragOver(folder.folder_id, e)
											}
											onDragLeave={
												isReordering
													? undefined
													: (e) => handleMoveDragLeave(folder.folder_id, e)
											}
											onDrop={
												isReordering
													? (e) => handleDrop("folder", idx, e)
													: (e) => handleMoveDrop(folder.folder_id, e)
											}
											onDragEnd={handleDragEnd}
											className={`bg-card border-border rounded-sm transition-colors group ${isReordering ? "cursor-grab active:cursor-grabbing" : "hover:border-volt/30 cursor-pointer"} ${isDragOver ? "ring-1 ring-volt border-volt/50" : ""}`}
											onClick={
												!isReordering
													? () => setCurrentFolderId(folder.folder_id)
													: undefined
											}
										>
											<CardContent
												className={`${isFolderListMode ? "p-2.5" : "p-4"} flex items-center gap-2.5`}
											>
												{isReordering && (
													<GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
												)}
												<FolderOpen
													className={`${isFolderListMode ? "w-5 h-5" : "w-8 h-8"} text-volt shrink-0`}
												/>
												{isFolderListMode ? (
													<div className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-12 gap-1.5 lg:gap-3 items-center">
														<div className="lg:col-span-4 min-w-0 flex items-center gap-2">
															<p className="font-heading text-sm leading-tight break-words line-clamp-2 lg:line-clamp-1">
																{folder.name}
															</p>
															<ShareBadge item={folder} t={t} />
														</div>
														<div className="lg:col-span-4 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
															<span>
																{subFolderCount} {t("programFolderCount")}
															</span>
															<span>
																{programCount} {t("programCount")}
															</span>
															<span className="truncate max-w-[200px]">
																{folder.coach_name || folder.coach_id || "-"}
															</span>
														</div>
														<div className="lg:col-span-4 text-[11px] text-muted-foreground lg:text-right truncate">
															<span>
																{formatCreatedDate(folder.created_at)}
															</span>
															{folder.visibility_reason ? (
																<span className="block text-volt/80 truncate">
																	{getVisibilityReasonLabel(folder)}
																</span>
															) : null}
														</div>
													</div>
												) : (
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2">
															<p className="font-heading text-sm break-words line-clamp-2">
																{folder.name}
															</p>
															<ShareBadge item={folder} t={t} />
														</div>
														<p className="text-[10px] text-muted-foreground">
															{subFolderCount} {t("programFolderCount")} ·{" "}
															{programCount} {t("programCount")}
														</p>
														<p className="text-[10px] text-muted-foreground/80 truncate">
															{t("createdByLabel")}:{" "}
															{folder.coach_name || folder.coach_id || "-"}
														</p>
														<p className="text-[10px] text-muted-foreground/80 truncate">
															{t("createdOnLabel")}:{" "}
															{formatCreatedDate(folder.created_at)}
														</p>
														{folder.visibility_reason && (
															<p className="text-[10px] text-volt/80 truncate">
																{getVisibilityReasonLabel(folder)}
																{folder.shared_by?.name &&
																folder.visibility_reason !== "owner"
																	? ` · da ${folder.shared_by.name}`
																	: ""}
															</p>
														)}
													</div>
												)}
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className={`rounded-sm h-7 w-7 ${isFolderListMode ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
															onClick={(e) => e.stopPropagation()}
														>
															<MoreVertical className="w-4 h-4" />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent className="bg-card border-border">
														{canManageFolder ? (
															<>
																<DropdownMenuItem
																	onClick={(e) => {
																		e.stopPropagation();
																		openFolderDialog(folder);
																	}}
																>
																	<Pencil className="w-3.5 h-3.5 mr-2" />
																	{t("rename")}
																</DropdownMenuItem>
																<DropdownMenuItem
																	onClick={(e) => {
																		e.stopPropagation();
																		setMovingFolderId(folder.folder_id);
																	}}
																>
																	<FolderInput className="w-3.5 h-3.5 mr-2" />
																	{t("moveToFolder")}
																</DropdownMenuItem>
																<DropdownMenuItem
																	onClick={(e) =>
																		openFolderShareDialog(folder, e)
																	}
																>
																	<UserPlus className="w-3.5 h-3.5 mr-2" />
																	{t("programSharing")}
																</DropdownMenuItem>
																<DropdownMenuItem
																	className="text-blaze"
																	onClick={(e) =>
																		handleDeleteFolder(folder.folder_id, e)
																	}
																>
																	<Trash2 className="w-3.5 h-3.5 mr-2" />
																	{t("delete")}
																</DropdownMenuItem>
															</>
														) : (
															<DropdownMenuItem disabled>
																{t("programVisibleViaSharing")}
															</DropdownMenuItem>
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											</CardContent>
										</Card>
									);
								})(),
							)}
						</div>
					)}

					{currentPrograms.length > 0 ? (
						<div
							className={
								viewMode === "grid"
									? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
									: "space-y-3"
							}
						>
							{currentPrograms.map((program, idx) => {
								const weeksCount =
									program.weeks_count ?? program.weeks?.length ?? 0;
								const athleteCount = program.athlete_ids?.length || 0;
								// From the compact projection: how many weeks athletes see now.
								// "4 weeks" alone said nothing about whether anyone sees one.
								const visibleWeeksCount =
									typeof program.active_weeks_count === "number"
										? program.active_weeks_count
										: null;
								const reachesSomeone =
									athleteCount > 0 || (program.group_ids || []).length > 0;
								const isListMode = viewMode === "list";
								const domain = programDomainLabel(program.domains);
								const domainLabel =
									domain === "Fisico" && !hasShootingDomain(user)
										? null
										: domain
											? t(
													{
														Fisico: "programDomainPhysical",
														Tiro: "programDomainShooting",
														Ibrido: "programDomainHybrid",
													}[domain],
												)
											: null;
								// L'ambito resta leggibile ma smette di gridare: pallino
								// colorato + testo muted, cosi' il nome del programma
								// e' la prima cosa che si legge.
								const domainDotClass = program.domains?.includes("shooting")
									? program.domains?.includes("physical")
										? "bg-purple-400/80"
										: "bg-red-400/80"
									: "bg-volt/80";
								const domainChip = domainLabel ? (
									<span className="shrink-0 inline-flex items-center gap-1 rounded-sm border border-border/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
										<span
											className={`w-1.5 h-1.5 rounded-full ${domainDotClass}`}
										/>
										{domainLabel}
									</span>
								) : null;
								const assignedCoaches =
									(program.assigned_coaches || []).length > 0
										? program.assigned_coaches
										: Array.from(
												new Set([
													...(program.coach_ids || []),
													...(program.coach_id ? [program.coach_id] : []),
												]),
											).map((coachId) => ({
												user_id: coachId,
												name: coachId,
											}));
								const assignedCoachTeams =
									(program.assigned_coach_teams || []).length > 0
										? program.assigned_coach_teams
										: Array.from(new Set(program.coach_team_ids || [])).map(
												(teamId) => ({
													team_id: teamId,
													name: teamId,
												}),
											);
								const programAthletes = athletes.filter((a) =>
									(program.athlete_ids || []).includes(a.user_id),
								);
								const programGroups = groups.filter((g) =>
									(program.group_ids || []).includes(g.group_id),
								);

								return (
									<Card
										key={program.program_id}
										draggable={isReordering || canEditProgram(program)}
										onDragStart={(e) =>
											handleDragStart("program", program.program_id, idx, e)
										}
										onDragOver={
											isReordering
												? (e) =>
														handleDragOver("program", program.program_id, e)
												: undefined
										}
										onDrop={
											isReordering
												? (e) => handleDrop("program", idx, e)
												: undefined
										}
										onDragEnd={handleDragEnd}
										className={`relative overflow-hidden bg-card border-border rounded-sm transition-colors ${isListMode ? "" : "h-full"} ${isReordering ? "cursor-grab active:cursor-grabbing" : "hover:border-volt/30 cursor-pointer"} ${dragOverId === program.program_id && dragSrcRef.current?.type === "program" ? "ring-1 ring-volt border-volt/50" : ""}`}
										onClick={
											isReordering
												? undefined
												: (e) => openProgramFromCard(program, e)
										}
										data-testid={`program-card-${program.program_id}`}
									>
										<ProgramColorStripe color={program.color} />
										<CardContent className={isListMode ? "p-3" : "p-4 md:p-6"}>
											{isReordering && !isListMode && (
												<div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/50">
													<GripVertical className="w-4 h-4 text-muted-foreground" />
													<span className="text-xs text-muted-foreground">
														Trascina per riordinare
													</span>
												</div>
											)}

											{isListMode ? (
												<div className="flex items-center gap-3">
													{isReordering && (
														<GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
													)}
													<div className="flex-1 min-w-0 grid grid-cols-1 xl:grid-cols-12 gap-2 xl:gap-3 items-center">
														<div className="min-w-0 xl:col-span-4">
															{/* Il nome ha una riga tutta sua: su mobile va a capo
															    (max 2 righe), su desktop resta troncato. Badge e
															    ambito scendono sotto invece di rubargli spazio. */}
															<Link
																to={programHref(program)}
																state={getProgramBuilderReturnState()}
																className="font-heading text-base leading-tight block break-words line-clamp-2 xl:line-clamp-1 hover:text-volt transition-colors"
															>
																{program.title}
															</Link>
															<div className="flex items-center flex-wrap gap-1.5 mt-1">
																<Badge
																	className={`shrink-0 text-[10px] px-2 py-0.5 ${
																		program.type === "group"
																			? "bg-acid/20 text-acid"
																			: "bg-volt/20 text-volt"
																	} border-0`}
																>
																	{program.type === "group"
																		? t("group")
																		: t("individual")}
																</Badge>
																{domainChip}
																<ShareBadge item={program} t={t} />
															</div>
															{program.description ? (
																<p className="text-xs text-muted-foreground truncate mt-1">
																	{program.description}
																</p>
															) : null}
														</div>

														<div className="xl:col-span-5 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
															<span className="inline-flex items-center gap-1">
																<Layers className="w-3.5 h-3.5" />
																{weeksCount} {t("programWeeksShort")}
															</span>
															{visibleWeeksCount !== null &&
																(visibleWeeksCount > 0 ? (
																	<span className="inline-flex items-center gap-1">
																		<Eye className="w-3.5 h-3.5" />
																		{visibleWeeksCount}{" "}
																		{t("pbVisibleWeeksShort")}
																	</span>
																) : reachesSomeone ? (
																	<span className="inline-flex items-center gap-1 text-amber-300">
																		<EyeOff className="w-3.5 h-3.5" />
																		{t("pbNoVisibleWeeks")}
																	</span>
																) : null)}
															<span className="inline-flex items-center gap-1">
																<Users className="w-3.5 h-3.5" />
																{program.type === "group"
																	? `${programGroups.length} ${t("groups").toLowerCase()}`
																	: `${athleteCount} ${t("athletes").toLowerCase()}`}
															</span>
															<span className="hidden sm:inline-flex items-center gap-1">
																<UserCog className="w-3.5 h-3.5" />
																{assignedCoaches.length}{" "}
																{t("coach").toLowerCase()}
															</span>
															<span className="hidden sm:inline-flex items-center gap-1">
																<Building2 className="w-3.5 h-3.5" />
																{assignedCoachTeams.length}{" "}
																{t(
																	assignedCoachTeams.length === 1
																		? "team"
																		: "teams",
																).toLowerCase()}
															</span>
														</div>

														<div className="xl:col-span-3 text-xs text-muted-foreground xl:text-right truncate">
															<span className="inline-flex items-center gap-1">
																<Calendar className="w-3 h-3" />
																{formatCreatedDate(
																	program.updated_at || program.created_at,
																)}
															</span>
															{program.visibility_reason ? (
																<span className="block text-volt/80 truncate">
																	{getVisibilityReasonLabel(program)}
																</span>
															) : null}
														</div>
													</div>

													<div className="flex items-center gap-1.5 shrink-0">
														<Link
															to={programHref(program)}
															state={getProgramBuilderReturnState()}
															className="hidden sm:inline-flex items-center gap-1 text-volt text-xs font-medium px-2 py-1 rounded-sm hover:bg-volt/10"
														>
															{t("open")}
															<ChevronRight className="w-3.5 h-3.5" />
														</Link>
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon"
																	className="rounded-sm h-7 w-7"
																	data-testid={`program-menu-${program.program_id}`}
																>
																	<MoreVertical className="w-4 h-4" />
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent className="bg-card border-border">
																{canEditProgram(program) && (
																	<>
																		<DropdownMenuItem
																			onClick={(e) =>
																				openProgramRenameDialog(program, e)
																			}
																		>
																			<Pencil className="w-3.5 h-3.5 mr-2" />
																			{t("edit")}
																		</DropdownMenuItem>
																		<DropdownMenuItem
																			onClick={(e) => {
																				e.preventDefault();
																				e.stopPropagation();
																				setMoveProgramId(program.program_id);
																			}}
																		>
																			<FolderInput className="w-3.5 h-3.5 mr-2" />
																			Sposta in cartella
																		</DropdownMenuItem>
																		{renderProgramColorMenu(program)}
																	</>
																)}
																<DropdownMenuItem
																	onClick={(e) =>
																		handleExportPDF(program.program_id, e)
																	}
																	disabled={exportingId === program.program_id}
																>
																	<Download className="w-3.5 h-3.5 mr-2" />
																	{exportingId === program.program_id
																		? "Esporto..."
																		: "Esporta PDF"}
																</DropdownMenuItem>
																<DropdownMenuItem
																	onClick={(e) =>
																		handleDuplicateProgram(program, e)
																	}
																>
																	<Copy className="w-3.5 h-3.5 mr-2" />
																	{t("duplicateProgram")}
																</DropdownMenuItem>
																{canEditProgram(program) && (
																	<DropdownMenuItem
																		className="text-blaze"
																		onClick={(e) =>
																			handleDeleteProgram(program.program_id, e)
																		}
																		data-testid={`delete-program-${program.program_id}`}
																	>
																		<Trash2 className="w-3.5 h-3.5 mr-2" />
																		{t("delete")}
																	</DropdownMenuItem>
																)}
															</DropdownMenuContent>
														</DropdownMenu>
													</div>
												</div>
											) : (
												<div className="flex items-start justify-between gap-2 mb-3">
													<div className="flex-1 min-w-0">
														<Link
															to={programHref(program)}
															state={getProgramBuilderReturnState()}
															className="font-heading text-lg mb-1.5 hover:text-volt transition-colors block break-words"
														>
															{program.title}
														</Link>
														<div className="flex items-center flex-wrap gap-1.5">
															<Badge
																className={`${
																	program.type === "group"
																		? "bg-acid/20 text-acid"
																		: "bg-volt/20 text-volt"
																} border-0`}
															>
																{program.type === "group" ? (
																	<>
																		<Users className="w-3 h-3 mr-1" /> Gruppo
																	</>
																) : (
																	<>
																		<User className="w-3 h-3 mr-1" />{" "}
																		Individuale
																	</>
																)}
															</Badge>
															{domainChip}
															<ShareBadge item={program} t={t} />
														</div>
													</div>
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="rounded-sm h-8 w-8"
																data-testid={`program-menu-${program.program_id}`}
															>
																<MoreVertical className="w-4 h-4" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent className="bg-card border-border">
															{canEditProgram(program) && (
																<>
																	<DropdownMenuItem
																		onClick={(e) =>
																			openProgramRenameDialog(program, e)
																		}
																	>
																		<Pencil className="w-3.5 h-3.5 mr-2" />
																		{t("edit")}
																	</DropdownMenuItem>
																	<DropdownMenuItem
																		onClick={(e) => {
																			e.preventDefault();
																			e.stopPropagation();
																			setMoveProgramId(program.program_id);
																		}}
																	>
																		<FolderInput className="w-3.5 h-3.5 mr-2" />
																		Sposta in cartella
																	</DropdownMenuItem>
																	{renderProgramColorMenu(program)}
																</>
															)}
															<DropdownMenuItem
																onClick={(e) =>
																	handleExportPDF(program.program_id, e)
																}
																disabled={exportingId === program.program_id}
															>
																<Download className="w-3.5 h-3.5 mr-2" />
																{exportingId === program.program_id
																	? "Esporto..."
																	: "Esporta PDF"}
															</DropdownMenuItem>
															<DropdownMenuItem
																onClick={(e) =>
																	handleDuplicateProgram(program, e)
																}
															>
																<Copy className="w-3.5 h-3.5 mr-2" />
																{t("duplicateProgram")}
															</DropdownMenuItem>
															{canEditProgram(program) && (
																<DropdownMenuItem
																	className="text-blaze"
																	onClick={(e) =>
																		handleDeleteProgram(program.program_id, e)
																	}
																	data-testid={`delete-program-${program.program_id}`}
																>
																	<Trash2 className="w-3.5 h-3.5 mr-2" />
																	{t("delete")}
																</DropdownMenuItem>
															)}
														</DropdownMenuContent>
													</DropdownMenu>
												</div>
											)}

											{!isListMode && (
												<>
													{program.description && (
														<p className="text-sm text-muted-foreground mb-4 line-clamp-2">
															{program.description}
														</p>
													)}
													{/* Info row: weeks + athletes/groups */}
													<div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground mb-4">
														<div className="flex items-center gap-1">
															<Layers className="w-4 h-4" />
															<span>
																{weeksCount}{" "}
																{weeksCount === 1 ? "settimana" : "settimane"}
															</span>
														</div>
														{visibleWeeksCount !== null &&
															(visibleWeeksCount > 0 ? (
																<span className="inline-flex items-center gap-1">
																	<Eye className="w-4 h-4" />
																	{visibleWeeksCount} {t("pbVisibleWeeksShort")}
																</span>
															) : reachesSomeone ? (
																<span className="inline-flex items-center gap-1 text-amber-300">
																	<EyeOff className="w-4 h-4" />
																	{t("pbNoVisibleWeeks")}
																</span>
															) : null)}

														{program.type !== "group" && athleteCount > 0 && (
															<Popover>
																<PopoverTrigger asChild>
																	<button
																		type="button"
																		className="flex items-center gap-1 hover:text-volt transition-colors"
																	>
																		<Users className="w-4 h-4" />
																		<span>
																			{athleteCount}{" "}
																			{athleteCount === 1 ? "atleta" : "atleti"}
																		</span>
																	</button>
																</PopoverTrigger>
																<PopoverContent
																	className="w-64 p-0 bg-card border-border"
																	align="start"
																>
																	<div className="p-3 border-b border-border">
																		<p className="label-tactical">
																			Atleti Assegnati
																		</p>
																	</div>
																	<ScrollArea className="max-h-[200px]">
																		<div className="p-2 space-y-1">
																			{programAthletes.map((a) => (
																				<div
																					key={a.user_id}
																					className="flex items-center gap-2 px-2 py-1.5 rounded-sm"
																				>
																					<Avatar className="w-6 h-6">
																						<AvatarImage src={a.picture} />
																						<AvatarFallback className="bg-volt/20 text-volt text-xs">
																							{a.name?.charAt(0).toUpperCase()}
																						</AvatarFallback>
																					</Avatar>
																					<div className="min-w-0">
																						<p className="text-sm truncate">
																							{a.name}
																						</p>
																						<p className="text-xs text-muted-foreground truncate">
																							{a.email}
																						</p>
																					</div>
																				</div>
																			))}
																		</div>
																	</ScrollArea>
																</PopoverContent>
															</Popover>
														)}
														{program.type !== "group" && athleteCount === 0 && (
															<div className="flex items-center gap-1">
																<Users className="w-4 h-4" />
																<span>0 atleti</span>
															</div>
														)}

														<Popover>
															<PopoverTrigger asChild>
																<button
																	type="button"
																	className="flex items-center gap-1 hover:text-volt transition-colors"
																>
																	<UserCog className="w-4 h-4" />
																	<span>{assignedCoaches.length} coach</span>
																</button>
															</PopoverTrigger>
															<PopoverContent
																className="w-64 p-0 bg-card border-border"
																align="start"
															>
																<div className="p-3 border-b border-border">
																	<p className="label-tactical">
																		Coach con accesso
																	</p>
																</div>
																<ScrollArea className="max-h-[180px]">
																	<div className="p-2 space-y-1">
																		{assignedCoaches.length > 0 ? (
																			assignedCoaches.map((coach) => (
																				<div
																					key={coach.user_id}
																					className="px-2 py-1.5 rounded-sm bg-secondary"
																				>
																					<p className="text-sm truncate">
																						{coach.name}
																					</p>
																					{coach.email && (
																						<p className="text-xs text-muted-foreground truncate">
																							{coach.email}
																						</p>
																					)}
																				</div>
																			))
																		) : (
																			<p className="text-xs text-muted-foreground px-2 py-2">
																				Nessun coach assegnato
																			</p>
																		)}
																	</div>
																</ScrollArea>
															</PopoverContent>
														</Popover>

														<Popover>
															<PopoverTrigger asChild>
																<button
																	type="button"
																	className="flex items-center gap-1 hover:text-acid transition-colors"
																>
																	<Building2 className="w-4 h-4" />
																	<span>{assignedCoachTeams.length} team</span>
																</button>
															</PopoverTrigger>
															<PopoverContent
																className="w-64 p-0 bg-card border-border"
																align="start"
															>
																<div className="p-3 border-b border-border">
																	<p className="label-tactical">
																		Coach Team con accesso
																	</p>
																</div>
																<ScrollArea className="max-h-[180px]">
																	<div className="p-2 space-y-1">
																		{assignedCoachTeams.length > 0 ? (
																			assignedCoachTeams.map((team) => (
																				<div
																					key={team.team_id}
																					className="px-2 py-1.5 rounded-sm bg-secondary"
																				>
																					<p className="text-sm truncate">
																						{team.name}
																					</p>
																				</div>
																			))
																		) : (
																			<p className="text-xs text-muted-foreground px-2 py-2">
																				Nessun coach team assegnato
																			</p>
																		)}
																	</div>
																</ScrollArea>
															</PopoverContent>
														</Popover>

														{program.type === "group" &&
															programGroups.length > 0 && (
																<Popover>
																	<PopoverTrigger asChild>
																		<button
																			type="button"
																			className="flex items-center gap-1 hover:text-acid transition-colors"
																		>
																			<FolderOpen className="w-4 h-4" />
																			<span>
																				{programGroups.length}{" "}
																				{programGroups.length === 1
																					? "gruppo"
																					: "gruppi"}
																			</span>
																		</button>
																	</PopoverTrigger>
																	<PopoverContent
																		className="w-72 p-0 bg-card border-border"
																		align="start"
																	>
																		<div className="p-3 border-b border-border">
																			<p className="label-tactical">
																				Gruppi Assegnati
																			</p>
																		</div>
																		<div className="p-2 space-y-2">
																			{programGroups.map((g) => {
																				const memberAthletes = athletes.filter(
																					(a) =>
																						(g.athlete_ids || []).includes(
																							a.user_id,
																						),
																				);
																				return (
																					<div
																						key={g.group_id}
																						className="p-2 bg-secondary/50 rounded-sm"
																					>
																						<div className="flex items-center gap-2 mb-1">
																							<FolderOpen className="w-4 h-4 text-acid" />
																							<span className="font-heading text-sm">
																								{g.name}
																							</span>
																						</div>
																						{g.description && (
																							<p className="text-xs text-muted-foreground mb-2">
																								{g.description}
																							</p>
																						)}
																						<div className="flex flex-wrap gap-1">
																							{memberAthletes.map((a) => (
																								<Badge
																									key={a.user_id}
																									variant="secondary"
																									className="rounded-sm text-xs"
																								>
																									{a.name}
																								</Badge>
																							))}
																							{memberAthletes.length === 0 && (
																								<span className="text-xs text-muted-foreground">
																									Nessun atleta
																								</span>
																							)}
																						</div>
																					</div>
																				);
																			})}
																		</div>
																	</PopoverContent>
																</Popover>
															)}
														{program.type === "group" &&
															programGroups.length === 0 && (
																<div className="flex items-center gap-1">
																	<FolderOpen className="w-4 h-4" />
																	<span>Nessun gruppo</span>
																</div>
															)}
													</div>
													<div className="space-y-1 text-xs text-muted-foreground mb-3">
														<p className="truncate">
															{t("createdByLabel")}:{" "}
															{program.coach_name || program.coach_id || "-"}
														</p>
														{/* Su mobile conta la data di aggiornamento:
														    la creazione resta su schermi piu' larghi. */}
														<p className="hidden sm:flex items-center gap-1 truncate">
															<Calendar className="w-3 h-3" />
															{t("createdOnLabel")}:{" "}
															{formatCreatedDate(program.created_at)}
														</p>
														<p className="flex items-center gap-1 truncate">
															<Calendar className="w-3 h-3" />
															{t("updatedOnLabel")}:{" "}
															{formatCreatedDate(
																program.updated_at || program.created_at,
															)}
														</p>
														{program.visibility_reason && (
															<p className="truncate text-volt/80">
																{getVisibilityReasonLabel(program)}
																{program.shared_by?.name &&
																program.visibility_reason !== "owner"
																	? ` · da ${program.shared_by.name}`
																	: ""}
															</p>
														)}
													</div>

													<div className="flex items-center justify-end">
														<Link
															to={programHref(program)}
															state={getProgramBuilderReturnState()}
															className="flex items-center text-volt text-sm font-medium hover:underline"
														>
															Apri
															<ChevronRight className="w-4 h-4" />
														</Link>
													</div>
												</>
											)}
										</CardContent>
									</Card>
								);
							})}
						</div>
					) : programColorFilter !== "all" ? (
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="flex flex-col items-center justify-center py-10 text-center">
								<Palette className="w-10 h-10 text-muted-foreground mb-3" />
								<p className="text-sm text-muted-foreground mb-4">
									{t("programColorNoMatch")}
								</p>
								<Button
									variant="outline"
									className="rounded-sm"
									onClick={() => setProgramColorFilter("all")}
								>
									{t("programColorFilterAll")}
								</Button>
							</CardContent>
						</Card>
					) : (
						currentFolders.length === 0 && (
							<Card className="bg-card border-border rounded-sm">
								<CardContent className="flex flex-col items-center justify-center py-12 text-center">
									<ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
									<p className="font-heading text-lg mb-2">
										{currentFolderId ? t("emptyFolder") : t("noPrograms")}
									</p>
									<p className="text-sm text-muted-foreground mb-4">
										{currentFolderId
											? t("addToFolder")
											: t("createFirstProgram")}
									</p>
									<Button
										onClick={() => setIsCreateProgramOpen(true)}
										className="bg-volt hover:bg-volt/90 rounded-sm"
									>
										<Plus className="w-4 h-4 mr-2" />
										{t("createProgram")}
									</Button>
								</CardContent>
							</Card>
						)
					)}
				</>
			)}{" "}
			{/* end !showTrash */}
			<Dialog
				open={isProgramRenameOpen}
				onOpenChange={(open) => {
					setIsProgramRenameOpen(open);
					if (!open) {
						setRenamingProgram(null);
						setProgramRenameTitle("");
						setProgramRenameDescription("");
						setProgramRenameColor(null);
					}
				}}
			>
				<DialogContent className="bg-card border-border max-w-sm">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("editProgram")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label className="label-tactical">{t("titleLabel")}</Label>
							<Input
								value={programRenameTitle}
								onChange={(e) => setProgramRenameTitle(e.target.value)}
								className="bg-secondary border-transparent rounded-sm"
								placeholder={t("programTitlePlaceholder")}
								autoFocus
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										handleRenameProgram();
									}
								}}
							/>
						</div>
						<div className="space-y-2">
							<Label className="label-tactical">{t("description")}</Label>
							<Textarea
								value={programRenameDescription}
								onChange={(e) => setProgramRenameDescription(e.target.value)}
								className="bg-secondary border-transparent rounded-sm min-h-[80px]"
								placeholder={t("programDescriptionPlaceholder")}
							/>
						</div>
						<div className="space-y-1.5">
							<div className="flex items-center gap-2">
								<Label className="label-tactical">{t("programColor")}</Label>
								<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
									{t("programColorOptional")}
								</span>
							</div>
							<ProgramColorPicker
								value={programRenameColor}
								onChange={setProgramRenameColor}
							/>
						</div>
						<Button
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
							onClick={handleRenameProgram}
							disabled={!programRenameTitle.trim()}
						>
							{t("save")}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			{/* ── CESTINO ── */}
			{showTrash && (
				<div>
					<div className="flex items-center justify-between gap-3 mb-5">
						<div className="flex items-center gap-3">
							<Trash2 className="w-5 h-5 text-blaze" />
							<div>
								<p className="font-heading text-lg">{t("trashBin")}</p>
								<p className="text-sm text-muted-foreground">
									{t("trashBinDesc")}
								</p>
							</div>
						</div>
						<Button
							variant="outline"
							className="rounded-sm border-border"
							onClick={() => setShowTrash(false)}
						>
							<ArrowLeft className="w-4 h-4 mr-2" />
							{t("backToPrograms")}
						</Button>
					</div>
					{loadingTrash ? (
						<div className="flex items-center justify-center py-12 text-muted-foreground">
							<Loader2 className="w-6 h-6 animate-spin mr-2" />
						</div>
					) : trashedPrograms.length === 0 ? (
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="flex flex-col items-center justify-center py-12 text-center">
								<Trash2 className="w-12 h-12 text-muted-foreground mb-4" />
								<p className="font-heading text-lg mb-1">
									{t("noDeletedPrograms")}
								</p>
								<p className="text-sm text-muted-foreground">
									{t("noDeletedProgramsDesc")}
								</p>
							</CardContent>
						</Card>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{trashedPrograms.map((program) => (
								<Card
									key={program.program_id}
									className="bg-card border-border rounded-sm border-blaze/20"
								>
									<CardContent className="p-5">
										<div className="flex items-start justify-between mb-2">
											<div className="flex-1 min-w-0">
												<p className="font-heading text-base truncate opacity-70">
													{program.title}
												</p>
												{program.deleted_at && (
													<p className="text-[10px] text-muted-foreground mt-0.5">
														{t("deletedOn")}: {program.deleted_at?.slice(0, 10)}
													</p>
												)}
											</div>
										</div>
										{program.description && (
											<p className="text-xs text-muted-foreground mb-3 line-clamp-2">
												{program.description}
											</p>
										)}
										<div className="flex gap-2 mt-3">
											<Button
												size="sm"
												variant="outline"
												className="flex-1 rounded-sm border-volt/40 text-volt hover:bg-volt/10 text-xs"
												onClick={(e) =>
													handleRestoreProgram(program.program_id, e)
												}
											>
												<RotateCcw className="w-3.5 h-3.5 mr-1.5" />
												{t("restoreProgram")}
											</Button>
											<Button
												size="sm"
												variant="outline"
												className="rounded-sm border-blaze/40 text-blaze hover:bg-blaze/10 text-xs"
												onClick={(e) =>
													handlePermanentDelete(program.program_id, e)
												}
											>
												<Trash2 className="w-3.5 h-3.5" />
											</Button>
										</div>
									</CardContent>
								</Card>
							))}
						</div>
					)}
				</div>
			)}
			{/* Folder Create/Rename Dialog */}
			<Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
				<DialogContent className="bg-card border-border max-w-sm">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{editingFolder ? t("renameFolder") : t("newFolder")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<Input
							value={folderName}
							onChange={(e) => setFolderName(e.target.value)}
							className="bg-secondary border-transparent rounded-sm"
							placeholder="Nome cartella"
							autoFocus
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									editingFolder ? handleRenameFolder() : handleCreateFolder();
								}
							}}
						/>
						{currentFolderId && !editingFolder && (
							<p className="text-xs text-muted-foreground">
								In:{" "}
								<span className="text-foreground">
									{breadcrumb.map((f) => f.name).join(" / ")}
								</span>
							</p>
						)}
						<Button
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
							onClick={editingFolder ? handleRenameFolder : handleCreateFolder}
							disabled={!folderName.trim()}
						>
							{editingFolder ? "Salva" : "Crea Cartella"}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			{/* Folder Sharing Dialog */}
			<Dialog open={isFolderShareOpen} onOpenChange={setIsFolderShareOpen}>
				<DialogContent className="bg-card border-border max-w-lg">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("folderSharingTitle")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							{sharingFolder?.name || "Cartella"}
						</p>
						<div className="space-y-2">
							<Label className="label-tactical">Coach con accesso</Label>
							<div className="max-h-36 overflow-y-auto space-y-1 p-2 bg-secondary rounded-sm">
								{(programAccessOptions.coaches || []).map((coach) => (
									<label
										key={coach.user_id}
										className="flex items-center gap-2 text-sm"
									>
										<input
											type="checkbox"
											checked={folderCoachSelection.includes(coach.user_id)}
											onChange={() => toggleFolderCoach(coach.user_id)}
										/>
										<span>{coach.name || coach.user_id}</span>
									</label>
								))}
							</div>
						</div>
						<div className="space-y-2">
							<Label className="label-tactical">Coach team con accesso</Label>
							<div className="max-h-36 overflow-y-auto space-y-1 p-2 bg-secondary rounded-sm">
								{(programAccessOptions.teams || []).map((team) => (
									<label
										key={team.team_id}
										className="flex items-center gap-2 text-sm"
									>
										<input
											type="checkbox"
											checked={folderCoachTeamSelection.includes(team.team_id)}
											onChange={() => toggleFolderCoachTeam(team.team_id)}
										/>
										<span>{team.name || team.team_id}</span>
									</label>
								))}
							</div>
						</div>
						<Button
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
							onClick={handleSaveFolderSharing}
						>
							{t("saveSharing")}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			{/* Move Program / Folder Dialog */}
			<Dialog
				open={!!movingItem}
				onOpenChange={(open) => !open && closeMoveDialog()}
			>
				<DialogContent className="bg-card border-border max-w-sm">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("moveToFolder")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-2 max-h-64 overflow-y-auto">
						<button
							className="w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-volt/10 flex items-center gap-2 disabled:opacity-40 disabled:pointer-events-none"
							disabled={!canDropOn(movingItem, null)}
							onClick={() => handleMoveFromDialog(null)}
						>
							<Home className="w-4 h-4 text-muted-foreground" />
							{t("rootNoFolder")}
						</button>
						{allFolderOptions.map((f) => (
							<button
								key={f.folder_id}
								className="w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-volt/10 flex items-center gap-2 disabled:opacity-40 disabled:pointer-events-none"
								disabled={!canDropOn(movingItem, f.folder_id)}
								onClick={() => handleMoveFromDialog(f.folder_id)}
							>
								<FolderOpen className="w-4 h-4 text-volt" />
								{f.path}
							</button>
						))}
					</div>
				</DialogContent>
			</Dialog>
			<AgentChatSheet
				open={isAiOpen}
				onOpenChange={setIsAiOpen}
				title="Crea un programma"
				description="Descrivi cosa ti serve: la bozza nasce qui, non pubblicata."
				onProgramCreated={() => loadData?.()}
			/>
		</div>
	);
}
